import React, { useState } from 'react';
import { useCallTranscription } from '../hooks/useCallTranscription';

/**
 * IncomingCallPopup — replaces the green banner in Layout.js
 *
 * Three render states:
 * 1. No screen_pop → fallback green banner (existing behavior)
 * 2. Known caller → rich panel with cases, trust alerts, actions
 * 3. Unknown caller → panel with "New Caller" and intake option
 */
export default function IncomingCallPopup({ incomingCall, onAnswer, onDismiss, onStartIntake }) {
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [caseSelectOpen, setCaseSelectOpen] = useState(false);

  // Derive call connection status
  const callConnected = callData?.status === 'connected' || callData?.status === 'answered';

  // Live transcription hook
  const { isConnected: txConnected, isCapturing, transcript } = useCallTranscription({
    callId: callData?.call_id,
    sessionId: callData?.session_id,
    enabled: callConnected,
    onTranscript: null,
  });

  if (!incomingCall) return null;

  const { callerInfo, callData } = incomingCall;
  const screenPop = callData?.screen_pop;
  const caller = screenPop?.caller;

  // ── STATE 1: No screen_pop — fallback green banner ──
  if (!screenPop) {
    return (
      <div className="fixed inset-x-0 top-0 z-[9999] animate-pulse">
        <div className="bg-green-600 text-white px-6 py-4 flex items-center justify-between shadow-2xl">
          <div className="flex items-center gap-4">
            <div className="text-3xl">📞</div>
            <div>
              <div className="text-lg font-bold">Incoming Call</div>
              <div className="text-sm opacity-90">{callerInfo}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onAnswer}
              className="bg-white text-green-700 font-bold px-6 py-2 rounded-lg hover:bg-green-50 transition-colors text-lg"
            >
              Open Phone
            </button>
            <button
              onClick={onDismiss}
              className="bg-green-700 text-white px-4 py-2 rounded-lg hover:bg-green-800 transition-colors"
            >
              Dismiss
            </button>
          </div>
        </div>
      </div>
    );
  }

  const openCases = screenPop.open_cases || [];
  const trustAlerts = screenPop.trust_account_alerts || [];
  const suggestedActions = screenPop.suggested_actions || [];
  const recentCalls = screenPop.recent_calls || [];
  const conflictWarnings = screenPop.conflict_warnings || [];

  // Helper: format duration
  const fmtDuration = (secs) => {
    if (!secs) return '0s';
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  // Helper: format date
  const fmtDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const diffDays = Math.floor((now - d) / 86400000);
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Helper: days until deadline
  const daysUntil = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    const now = new Date();
    return Math.ceil((d - now) / 86400000);
  };

  // Helper: contact type display
  const contactTypeBadge = (type) => {
    if (!type) return null;
    const label = type.replace(/_/g, ' ').toUpperCase();
    const colors = {
      CLIENT: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      OPPOSING_COUNSEL: 'bg-red-500/20 text-red-400 border-red-500/30',
      'OPPOSING COUNSEL': 'bg-red-500/20 text-red-400 border-red-500/30',
      COURT: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      WITNESS: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      VENDOR: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
    };
    const cls = colors[label] || colors[type?.toUpperCase()] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    return (
      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded border ${cls}`}>
        {label}
      </span>
    );
  };

  // ── Shared panel wrapper ──
  const Panel = ({ children }) => (
    <div className="fixed bottom-4 right-4 z-[9999] w-[420px] max-h-[85vh] overflow-y-auto rounded-xl bg-gray-900 border border-gray-700 shadow-2xl shadow-black/50">
      {children}
    </div>
  );

  // ── Shared header ──
  const Header = ({ name, number, isKnown }) => (
    <div className="px-4 pt-4 pb-3 border-b border-gray-700/50">
      <div className="flex items-center gap-2 mb-2">
        <span className="relative flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
        </span>
        <span className="text-xs font-medium text-green-400 uppercase tracking-wider">Incoming Call</span>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {name ? (
          <>
            <span className="text-xl font-bold text-white">{name}</span>
            {caller?.conflict_flag && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-red-600 text-white animate-pulse">
                CONFLICT
              </span>
            )}
            {contactTypeBadge(caller?.contact_type)}
          </>
        ) : (
          <>
            <span className="text-xl font-bold text-white">{number || 'Unknown'}</span>
            {!isKnown && (
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-gray-600 text-gray-300 border border-gray-500">
                NEW CALLER
              </span>
            )}
          </>
        )}
      </div>
      {name && number && (
        <div className="text-sm text-gray-400 mt-1">{number}</div>
      )}
      {caller?.call_count > 0 && (
        <div className="text-xs text-gray-500 mt-1">
          {caller.call_count} previous call{caller.call_count !== 1 ? 's' : ''}
          {caller.last_call_date && ` · Last: ${fmtDate(caller.last_call_date)}`}
        </div>
      )}
    </div>
  );

  // ── Shared actions row ──
  const ActionsRow = ({ showIntake = false, showCaseLink = false }) => (
    <div className="px-4 py-3 border-t border-gray-700/50">
      <div className="flex gap-2">
        <button
          onClick={onAnswer}
          className="flex-1 bg-green-600 hover:bg-green-500 text-white font-semibold py-2 px-3 rounded-lg transition-colors text-sm"
        >
          Answer
        </button>
        <button
          onClick={onDismiss}
          className="flex-1 bg-red-600/80 hover:bg-red-500 text-white font-semibold py-2 px-3 rounded-lg transition-colors text-sm"
        >
          Decline
        </button>
        {showIntake && onStartIntake && (
          <button
            onClick={onStartIntake}
            className="flex-1 bg-blue-600 hover:bg-blue-500 text-white font-semibold py-2 px-3 rounded-lg transition-colors text-sm"
          >
            Intake
          </button>
        )}
        <button
          onClick={() => setNoteOpen(!noteOpen)}
          className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
            noteOpen ? 'bg-gray-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          Note
        </button>
        {showCaseLink && openCases.length > 0 && (
          <button
            onClick={() => setCaseSelectOpen(!caseSelectOpen)}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              caseSelectOpen ? 'bg-gray-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            To Case
          </button>
        )}
      </div>

      {/* Inline note textarea */}
      {noteOpen && (
        <textarea
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          placeholder="Add a note about this call..."
          className="mt-2 w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-gray-500 resize-none"
          rows={2}
          autoFocus
        />
      )}

      {/* Inline case selector */}
      {caseSelectOpen && openCases.length > 0 && (
        <select
          className="mt-2 w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-gray-500"
          defaultValue=""
        >
          <option value="" disabled>Link to case...</option>
          {openCases.map((c) => (
            <option key={c.id} value={c.id}>
              {c.case_number} — {c.title}
            </option>
          ))}
        </select>
      )}
    </div>
  );

  // ── STATE 2: Known caller ──
  if (caller?.is_known_contact) {
    return (
      <Panel>
        <Header
          name={caller.name}
          number={callData?.from_number}
          isKnown={true}
        />

        {/* Open Cases */}
        {openCases.length > 0 && (
          <div className="px-4 py-3 border-b border-gray-700/50">
            <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2">Open Cases</div>
            {openCases.slice(0, 3).map((c) => {
              const nextDeadline = c.upcoming_deadlines?.[0];
              const days = nextDeadline ? daysUntil(nextDeadline.date) : null;
              return (
                <div key={c.id} className="mb-2 last:mb-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-white truncate">{c.title || c.case_number}</span>
                    {c.case_number && c.title && (
                      <span className="text-[10px] text-gray-500 shrink-0">{c.case_number}</span>
                    )}
                  </div>
                  {nextDeadline && (
                    <div className={`text-xs mt-0.5 ${days !== null && days <= 7 ? 'text-red-400 font-medium' : 'text-gray-500'}`}>
                      Deadline: {nextDeadline.description} — {fmtDate(nextDeadline.date)}
                      {days !== null && days <= 7 && days >= 0 && ` (${days}d)`}
                    </div>
                  )}
                  {c.last_activity && (
                    <div className="text-xs text-gray-600 mt-0.5 truncate">
                      Last activity: {fmtDate(c.last_activity)}
                    </div>
                  )}
                </div>
              );
            })}
            {openCases.length > 3 && (
              <div className="text-xs text-gray-500 mt-1">+{openCases.length - 3} more</div>
            )}
          </div>
        )}

        {/* Trust Account Alerts */}
        {trustAlerts.length > 0 && (
          <div className="px-4 py-2 border-b border-gray-700/50">
            {trustAlerts.map((alert, i) => (
              <div key={i} className="flex items-center gap-2 py-1">
                <span className="text-yellow-400 text-xs">⚠</span>
                <span className="text-xs text-yellow-400 font-medium">{alert.alert}</span>
                {alert.case_number && (
                  <span className="text-[10px] text-gray-500">{alert.case_number}</span>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Conflict Warnings */}
        {conflictWarnings.length > 0 && (
          <div className="px-4 py-2 bg-red-900/20 border-b border-red-800/30">
            {conflictWarnings.map((w, i) => (
              <div key={i} className="text-xs text-red-400 py-0.5">{w}</div>
            ))}
          </div>
        )}

        {/* Suggested Actions */}
        {suggestedActions.length > 0 && (
          <div className="px-4 py-2 border-b border-gray-700/50">
            <div className="flex flex-wrap gap-1.5">
              {suggestedActions.slice(0, 3).map((action, i) => (
                <span key={i} className="text-[11px] px-2 py-1 rounded-full bg-gray-800 text-gray-300 border border-gray-700">
                  {action}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Recent Calls */}
        {recentCalls.length > 0 && (
          <div className="px-4 py-2 border-b border-gray-700/50">
            <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Recent</div>
            {recentCalls.slice(0, 2).map((call, i) => (
              <div key={i} className="flex items-center gap-2 py-0.5 text-xs text-gray-400">
                <span>{call.direction === 'Inbound' ? '↙' : '↗'}</span>
                <span>{fmtDate(call.start_time)}</span>
                <span className="text-gray-600">·</span>
                <span>{fmtDuration(call.duration)}</span>
                {call.result && <span className="text-gray-600">· {call.result}</span>}
              </div>
            ))}
          </div>
        )}

        <ActionsRow showCaseLink={true} />
        {/* Live transcript */}
        {isCapturing && (
          <div className="px-4 py-3 border-t border-gray-700/50">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                Live Transcript
              </span>
              {!txConnected && (
                <span className="text-[10px] text-yellow-500">connecting...</span>
              )}
            </div>
            <div className="max-h-32 overflow-y-auto space-y-1">
              {transcript.slice(-6).map((seg, i) => (
                <div key={i} className="text-sm text-gray-300 leading-relaxed">
                  {seg.text}
                </div>
              ))}
              {transcript.length === 0 && (
                <div className="text-gray-600 italic text-xs">Listening...</div>
              )}
            </div>
          </div>
        )}
      </Panel>
    );
  }

  // ── STATE 3: Unknown caller ──
  return (
    <Panel>
      <Header
        name={null}
        number={callData?.from_number}
        isKnown={false}
      />

      <div className="px-4 py-3 border-b border-gray-700/50">
        <div className="text-sm text-gray-500">No contact record found</div>
      </div>

      {/* Suggested Actions */}
      {suggestedActions.length > 0 && (
        <div className="px-4 py-2 border-b border-gray-700/50">
          <div className="flex flex-wrap gap-1.5">
            {suggestedActions.slice(0, 3).map((action, i) => (
              <span key={i} className="text-[11px] px-2 py-1 rounded-full bg-gray-800 text-gray-300 border border-gray-700">
                {action}
              </span>
            ))}
          </div>
        </div>
      )}

      <ActionsRow showIntake={true} />
        {/* Live transcript */}
        {isCapturing && (
          <div className="px-4 py-3 border-t border-gray-700/50">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                Live Transcript
              </span>
              {!txConnected && (
                <span className="text-[10px] text-yellow-500">connecting...</span>
              )}
            </div>
            <div className="max-h-32 overflow-y-auto space-y-1">
              {transcript.slice(-6).map((seg, i) => (
                <div key={i} className="text-sm text-gray-300 leading-relaxed">
                  {seg.text}
                </div>
              ))}
              {transcript.length === 0 && (
                <div className="text-gray-600 italic text-xs">Listening...</div>
              )}
            </div>
          </div>
        )}
    </Panel>
  );
}
